import {defineStore} from 'pinia'
import {BoardsNs, CommonNs, FeatureNs, ScreenNs} from "@shared/types";
import {FeatureApi} from "@/api";
import {AzureApi} from "@/api/azure.api";
import {ProjectPageApi} from "@/api/project-page.apis";
import {useSelectedProjectStore} from "@domains/boards";
import {colors} from "@shared/constants/common.constants";
import {removeItemLocalStorage, setLocalStorage} from "@shared/helpers";
import {convertJsonToMarkdown, extractSmartBlockLines, isTiptapDoc} from "@shared/markdown-parser";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useUserStore} from "@shared/stores/user.store";
import appRouter from '@/router';

export const useFeatureStore = defineStore("feature", {
  state: () => ({
    featureMap: new Map<string, FeatureNs.Feature>,
    featureTree: [] as FeatureNs.FeatureTreeNode[],
    selectedType: FeatureNs.NodeType.APP_DETAIL as FeatureNs.NodeType,
    selectedFeature: {} as FeatureNs.Feature | FeatureNs.ProjectPage,
    breadcrumb: [] as BoardsNs.Breadcrumb[],
    isTreeCollapsed: false,
    isSelectModeOn: false,
    isSearchModeOn: false,
    isEditorOpen: false,
    isAppDetailsSelected: false,
    projectPageMap: new Map<string, FeatureNs.ProjectPage>,
    projectPageTree: [] as FeatureNs.PageTreeNode[],
    showGenerateProjectModules: false, // read by the Feature band; see feature-tree.section.vue
    activeSearchTab: 'search' as string,
    waitingFeatureCount: 0,
    waitingFeatures: [] as FeatureNs.Feature[],
    wasEditorExpandedBeforeClose: false,
    // Bumped to ask the open editor to save what it still holds / to reload a description that
    // was written outside of it (an accepted chat suggestion).
    editorFlushTick: 0,
    editorReloadTick: 0
  }),
  getters: {
    getFeatureList: (state) => [...state.featureMap.values()],
    getProjectPageList: (state) => [...state.projectPageMap.values()],
    getFeatureTree: (state) => state.featureTree,
    getProjectPageTree: (state) => state.projectPageTree,
    getSelectedFeature: (state) => state.selectedFeature,
    getBreadcrumb: (state) => state.breadcrumb,
    getActiveSearchTab: (state) => state.activeSearchTab,
    getWaitingFeatures: (state) => state.waitingFeatures,
    getWaitingFeatureCount: (state) => state.waitingFeatureCount,
    getSelectedType: (state) => state.selectedType,
    getFeatureScreens: (state) => state.selectedFeature.screens
  },
  actions: {
    setSearchActiveTab(tab: string) {
      this.activeSearchTab = tab;  // Update the activeSearchTab state
    },

    convertFlatDataToTree() {
      const map = new Map<string, FeatureNs.FeatureTreeNode>;

      this.featureMap.forEach((item: FeatureNs.Feature, key: string) => {
        map.set(item.id, {...item, ...item.properties, children: []} as FeatureNs.FeatureTreeNode);
      });

      const hierarchy: FeatureNs.FeatureTreeNode[] = [];
      map.forEach((item: FeatureNs.FeatureTreeNode, key: string) => {
        if (item.parentId) {
          let isParentAGroup = false;
          const parentItem = map.get(item.parentId);
          if (parentItem) {
            isParentAGroup = parentItem.featureType === 'GROUP';
            // Find Parent Color
            let parentColor = parentItem.parentColor ?? parentItem.color;
            if (!parentColor) {
              // Move up one step
              if (parentItem.parentId) {
                isParentAGroup = true;
                parentColor = map.get(parentItem.parentId)!.color
              }
            }

            item.parentColor = parentColor || (isParentAGroup ? colors[0] : "");
            parentItem.children.push(item!);
          }
        } else {
          hierarchy.push(item!);
        }
      });

      hierarchy.forEach((item) => this.addInputTreeNode(item));

      this.featureTree = hierarchy;
      return hierarchy;
    },

    createInputTreeNode(parentId: string | number,
                        summary: string, parentColor: any,
                        hidden: boolean | undefined): FeatureNs.FeatureTreeNode {
      return {
        parentId,
        featureType: FeatureNs.FeatureTypeEnums.INPUT,
        summary,
        hidden,
        parentColor
      } as FeatureNs.FeatureTreeNode
    },

    addInputTreeNode(node: any) {
      if (node.featureType === FeatureNs.FeatureTypeEnums.GROUP) {
        node.children.forEach((item: any) => this.addInputTreeNode(item));
        node.children.push(this.createInputTreeNode(node.id, 'feature', node.color, node.hidden));
      } else if (node.featureType === FeatureNs.FeatureTypeEnums.FEATURE) {
        node.children.push(this.createInputTreeNode(node.id, 'subfeature', node.parentColor, node.hidden));
      }
    },

    getFeatureById(featureId: string): FeatureNs.Feature | undefined {
      return this.featureMap.get(featureId);
    },

    async loadAllFeature(
        payload: FeatureNs.GetAllFeatureRequest
    ): Promise<FeatureNs.Feature[]> {
      return FeatureApi.getAllFeature(payload)
      .then((featureList) => {
        this.featureMap.clear();
        featureList.forEach(item => {
          this.featureMap.set(item.id, item);
        });
        this.convertFlatDataToTree();
        return featureList
      })
    },

    async updateFeatureParent(
        payload: FeatureNs.UpdateFeatureParentRequest
    ): Promise<FeatureNs.Feature> {
      return FeatureApi.updateFeatureParent(payload)
      .then((response) => {
        this.refreshWaitingFeatures();
        return response;
      })
    },

    async showOrHideFeature(
        payload: FeatureNs.UpdateFeatureRequest
    ): Promise<CommonNs.AnyResponse> {
      return FeatureApi.updateShowOrHide(payload)
      .then((response) => {
        const isHidden = payload.feature.properties.hidden;
        payload.feature.ids.forEach((id: string) => {
          const item = this.featureMap.get(id);
          if (item) {
            if (!item.properties) {
              item.properties = [];
            }
            item.properties.hidden = isHidden;
          }
        });
        this.convertFlatDataToTree();
        return response
      })
    },

    async deleteFeature(payload: FeatureNs.UpdateFeatureRequest
    ): Promise<FeatureNs.Feature> {
      return FeatureApi.deleteFeature(payload)
      .then((response) => {
        payload.feature.ids.forEach((id: string) => this.featureMap.delete(id));
        this.convertFlatDataToTree();
        return response
      })
    },

    selectAFeature(payload: FeatureNs.Feature): void {
      if (this.selectedFeature !== payload) {
        this.selectedFeature = payload;

        const current = appRouter.currentRoute.value;
        const sameRow = current.params.featureId === payload.id;

        this.router.push({
          name: BoardsNs.BoardsRouteEnums.FEATURE_SELECTED,
          params: {featureId: payload.id},
          ...(sameRow ? {query: current.query} : {}),
        });

        this.selectedType = FeatureNs.NodeType.FEATURE;
        this.refreshBreadcrumb(payload);

        const projectStore = useSelectedProjectStore();
        projectStore.addItemToRecentlyViewed({type: 'FEATURE', item: payload});

        setLocalStorage('selectedFeature', payload.id);

        this.isEditorOpen = true;

        // 🔁 Restore Full View if it was set before close
        if (this.wasEditorExpandedBeforeClose) {
          const userStore = useUserStore();
          userStore.setBoardPreferences({
            board: 'FEATURE',
            preferences: 'isEditorExpanded',
            value: true
          });
          this.wasEditorExpandedBeforeClose = false;
        }
      }
    },

    refreshBreadcrumb(payload: FeatureNs.Feature | FeatureNs.ProjectPage) {
      const newBreadcrumb = [] as BoardsNs.Breadcrumb[]
      this.addToBreadcrumb(payload, newBreadcrumb);
      this.breadcrumb = newBreadcrumb;
    },

    clearSelectedFeature(): void {
      const userStore = useUserStore();

      // Store current state before resetting
      this.wasEditorExpandedBeforeClose = userStore.getUiPreferences.featureBoard.isEditorExpanded;

      // Temporarily reset UI view
      userStore.setBoardPreferences({
        board: 'FEATURE',
        preferences: 'isEditorExpanded',
        value: false
      });

      this.selectedFeature = {} as FeatureNs.Feature;
      this.isEditorOpen = false;

      this.router.push({
        name: BoardsNs.BoardsRouteEnums.FEATURE
      });

      removeItemLocalStorage('selectedFeature');
    },

    async addNewFeature(
        payload: FeatureNs.UpdateFeatureRequest, screen: ScreenNs.Screen = {} as ScreenNs.Screen
    ): Promise<FeatureNs.Feature[]> {
      return FeatureApi.addNewFeatureList(payload)
      .then((addedFeatures) => {
        addedFeatures.forEach((addedFeature) => {
          if (screen && screen.id) {
            const screenList: ScreenNs.Screen[] = [screen];
            if (addedFeature.screens && addedFeature.screens.length > 0) {
              screenList.push(...addedFeature.screens);
            }
            addedFeature.screens = screenList;
          }
          this.featureMap.set(addedFeature.id, addedFeature);
        })

        this.refreshWaitingFeatures();
        return addedFeatures
      })
    },

    async addGroupsWithFeatures(
        payload: FeatureNs.AddGroupsAndFeaturesRequest
    ): Promise<FeatureNs.Feature[]> {
      return FeatureApi.addGroupsWithFeatures(payload)
      .then((addedFeatures) => {
        addedFeatures.forEach((addedFeature) => this.featureMap.set(addedFeature.id, addedFeature))
        this.convertFlatDataToTree();
        this.refreshWaitingFeatures();
        return addedFeatures;
      })
    },

    async updateFeature(
        payload: FeatureNs.UpdateFeatureRequest
    ): Promise<CommonNs.AnyResponse> {
      return FeatureApi.updateFeature(payload)
      .then((response) => {
        this.refreshWaitingFeatures();
        return response;
      })
    },

    async duplicateFeature(
        payload: FeatureNs.UpdateFeatureRequest
    ): Promise<FeatureNs.Feature> {
      return FeatureApi.duplicateFeature(payload)
      .then((response) => {
        this.refreshWaitingFeatures();
        return response;
      })
    },

    async changeFeatureColor(
        payload: FeatureNs.UpdateFeatureRequest
    ): Promise<FeatureNs.Feature> {
      return FeatureApi.changeFeatureColor(payload)
      .then((response) => {
        const feature = this.featureMap.get(payload.feature.ids[0]);
        if (feature) {
          if (!feature.properties) {
            feature.properties = [];
          }
          feature.properties.color = payload.feature.properties.color;
        }

        this.convertFlatDataToTree();
        return response
      })
    },

    async createAGroup(
        payload: FeatureNs.UpdateFeatureRequest
    ): Promise<FeatureNs.Feature> {
      return FeatureApi.createAGroup(payload)
      .then((createdGroup) => {
        this.featureMap.set(createdGroup.id, createdGroup);
        this.refreshWaitingFeatures();
        return createdGroup;
      })
    },

    /** Description → markdown (CSV/export için). Güvenli. */
    descriptionToMarkdown(description: string | null | undefined): string {
      if (!description) return '';
      try {
        return convertJsonToMarkdown(JSON.parse(description));
      } catch (e) {
        // Legacy düz metin / HTML — olduğu gibi taşı.
        return description;
      }
    },

    convertToCSV(): string {
      const csvRows = [];

      const headers = ["Issue Type", "Summary", "Description", "Work item id", "Parent", "Created", "Creator",
        "Updated", "Assignee"];
      csvRows.push(headers.join(","));

      for (const page of this.projectPageMap.values()) {
        const values = [this.getFeatureTypeStr(page.featureType), page.summary,
          this.getEscapedValue(this.descriptionToMarkdown(page.description)),
          page.id,
          '',
          page.createDate,
          page.createdBy ? page.createdBy.fullName : '',
          page.updateDate,
          page.updatedBy ? page.updatedBy.fullName : ''];
        csvRows.push(values.join(","));
      }

      for (const feature of this.featureMap.values()) {
        const values = [this.getFeatureTypeStr(feature.featureType), feature.summary,
          this.getEscapedValue(this.descriptionToMarkdown(feature.description)),
          feature.id,
          feature.parentId,
          feature.createDate,
          feature.createdBy ? feature.createdBy.fullName : '',
          feature.updateDate,
          feature.updatedBy ? feature.updatedBy.fullName : ''];
        csvRows.push(values.join(","));
      }

      return csvRows.join("\n");
    },

    exportFeatureModules(): string {
      return this.featureTree
      .filter(node => node.featureType === 'GROUP')
      .map((node, index) => `${index + 1}. ${node.summary}`)
      .join('\n');
    },

    exportFeatureTree(options = {
      exportGroup: true,
      exportFeature: true,
      exportSubFeature: true,
      exportUserStory: false,
      exportAc: false
    }): string {
      return this.convertFeatureTreeToFlat(this.featureTree, '', options).join('\n');
    },

    convertFeatureTreeToFlat(
        tree: FeatureNs.FeatureTreeNode[],
        prefix = '',
        options = {
          exportGroup: true,
          exportFeature: true,
          exportSubFeature: true,
          exportUserStory: false,
          exportAc: false
        }
    ): string[] {
      return tree.flatMap((node, index) => {
        const {exportGroup, exportFeature, exportSubFeature, exportUserStory, exportAc} = options;
        const currentPrefix = prefix ? `${prefix}${index + 1}` : `${index + 1}.`;
        const lines: string[] = [];

        const shouldInclude =
            (node.featureType === 'GROUP' && exportGroup) ||
            (node.featureType === 'FEATURE' && exportFeature) ||
            (node.featureType === 'SUB_FEATURE' && exportSubFeature);

        if (shouldInclude) {
          if (node.featureType === 'GROUP') {
            lines.push('');
          }
          lines.push(`${currentPrefix} ${node.summary}`);

          const desc = node.description;
          if (desc && (exportUserStory || exportAc)) {
            try {
              const json = JSON.parse(desc);

              if (isTiptapDoc(json)) {
                // Tiptap: smartBlock attrs.kind ile çıkarım (Faz 4.2).
                if (exportUserStory) {
                  const storyLines = extractSmartBlockLines(json, 'userStory');
                  if (storyLines.length) {
                    lines.push(` User Story:`);
                    storyLines.forEach((l: string) => lines.push(`   ${l}`));
                  }
                }
                if (exportAc) {
                  const acLines = extractSmartBlockLines(json, 'acceptanceCriteria');
                  if (acLines.length) {
                    lines.push(` Acceptance Criteria:`);
                    acLines.forEach((l: string, idx: number) => lines.push(`   ${idx + 1}. ${l}`));
                  }
                }
              } else {
                // Legacy EditorJS: data.method + iç içe content.blocks.
                const blocks = json.blocks || [];

                blocks.forEach((block: any) => {
                  const method = block?.data?.method;
                  const content = block?.data?.content;

                  if (method === 'userStory' && exportUserStory && content?.blocks?.length) {
                    lines.push(` User Story:`);
                    content.blocks.forEach((b: any) => {
                      lines.push(`   ${b.data.text.replace(/<[^>]*>?/gm, '')}`);
                    });
                  }

                  if (method === 'acceptanceCriteria' && exportAc && content?.blocks?.length) {
                    const checklist = content.blocks.find((b: any) => b.type === 'checklist');
                    if (checklist?.data?.items?.length) {
                      lines.push(` Acceptance Criteria:`);
                      checklist.data.items.forEach((item: any, idx: number) => {
                        lines.push(`   ${idx + 1}. ${item.content.replace(/<[^>]*>?/gm, '')}`);
                      });
                    }
                  }
                });
              }
            } catch (e) {
              console.warn('Description JSON parse error:', e);
            }
          }
        }

        const childrenLines = node.children
            ? this.convertFeatureTreeToFlat(node.children, currentPrefix, options)
            : [];

        return [...lines, ...childrenLines];
      });
    },

    getFeatureTypeStr(featureType: FeatureNs.FeatureTypeEnums): string {
      switch (featureType) {
        case FeatureNs.FeatureTypeEnums.GROUP:
          return "Group (Epic)";
        case FeatureNs.FeatureTypeEnums.FEATURE:
          return "Feature (Task)";
        case FeatureNs.FeatureTypeEnums.SUB_FEATURE:
          return "Subfeature (Sub-task)";
        case FeatureNs.FeatureTypeEnums.PAGE:
          return "Page (Epic)";
        default:
          return "";
      }
    },

    getEscapedValue(value: string): string {
      // Check if the value contains newlines, commas, or quotes
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');
        // Wrap the field in double quotes
        return `"${value}"`;
      }
      return value;
    },

    addToBreadcrumb(payload: FeatureNs.Feature | FeatureNs.ProjectPage, newBreadcrumb: BoardsNs.Breadcrumb[]) {
      newBreadcrumb.unshift({
        id: payload.id,
        summary: payload.summary,
        type: payload.featureType
      } as BoardsNs.Breadcrumb)

      if ((<FeatureNs.Feature>payload).parentId) {
        const parent = this.featureMap.get((<FeatureNs.Feature>payload).parentId);
        if (parent !== undefined) {
          this.addToBreadcrumb(parent, newBreadcrumb)
        }
      }
    },

    switchTreeCollapsedValue(): void {
      this.isTreeCollapsed = !this.isTreeCollapsed;
    },

    setSelectMode(isSelectModeOn: boolean): void {
      this.isSelectModeOn = isSelectModeOn;
    },

    setSearchMode(isSearchModeOn: boolean): void {
      this.isSearchModeOn = isSearchModeOn;
    },

    setIsEditorOpen(isEditorOpen: boolean): void {
      if (this.isEditorOpen !== isEditorOpen) {
        this.isEditorOpen = isEditorOpen;
      }
    },


    async loadAllProjectPage(
        payload: FeatureNs.GetAllProjectPageRequest
    ): Promise<FeatureNs.ProjectPage[]> {
      return ProjectPageApi.getAllProjectPage(payload)
      .then((projectPageList) => {
        this.projectPageMap.clear();
        projectPageList.forEach(item => {
          item.featureType = FeatureNs.FeatureTypeEnums.PAGE
          this.projectPageMap.set(item.id, item);
        });
        this.convertFlatDataToPageTree()
        return projectPageList
      })
    },

    /** Bulk insert — BUILDER /insert akışı tarafından çağrılır. */
    async addProjectPagesBulk(
        payload: FeatureNs.UpdateProjectPageRequest
    ): Promise<FeatureNs.ProjectPage[]> {
      return ProjectPageApi.addProjectPagesBulk(payload)
      .then((addedPages) => {
        addedPages.forEach((page) => {
          page.featureType = FeatureNs.FeatureTypeEnums.PAGE;
          this.projectPageMap.set(page.id, page);
        });
        this.convertFlatDataToPageTree();
        return addedPages;
      })
    },

    requestEditorFlush(): void {
      this.editorFlushTick++;
    },

    /**
     * A description changed on the server while its element may be open. The open editor keeps
     * its own copy and saves the whole document, so it has to take the new one or its next save
     * would undo the change.
     */
    syncSelectedDescription(id: string): void {
      const fresh = this.featureMap.get(id) ?? this.projectPageMap.get(id);
      if (!fresh || this.selectedFeature?.id !== id) return;
      this.selectedFeature.description = fresh.description;
      this.editorReloadTick++;
    },

    getProjectPageById(pageId: string): FeatureNs.ProjectPage | undefined {
      return this.projectPageMap.get(pageId);
    },

    selectAPage(payload: FeatureNs.ProjectPage): void {
      if (this.selectedFeature?.id !== payload.id) {
        this.selectedFeature = payload;

        const current = appRouter.currentRoute.value;
        const samePage = current.params.pageId === payload.id;

        this.router.push({
          name: BoardsNs.BoardsRouteEnums.PAGE_SELECTED,
          params: {pageId: payload.id},
          ...(samePage ? {query: current.query} : {}),
        });

        this.selectedType = FeatureNs.NodeType.PAGE;
        this.refreshBreadcrumb(payload);

        this.isEditorOpen = true;

        // 🔁 Restore Full View if it was set before close
        if (this.wasEditorExpandedBeforeClose) {
          const userStore = useUserStore();
          userStore.setBoardPreferences({
            board: 'FEATURE',
            preferences: 'isEditorExpanded',
            value: true
          });
          this.wasEditorExpandedBeforeClose = false;
        }
      }
    },

    async showOrHideProjectPage(
        payload: FeatureNs.UpdateProjectPageRequest
    ): Promise<FeatureNs.ProjectPage> {
      return ProjectPageApi.updateProjectPage(payload)
      .then((response) => {
        const isHidden = payload.projectPage.properties.hidden;
        payload.projectPage.ids.forEach((id: string) => {
          const item = this.projectPageMap.get(id);
          if (item) {
            if (!item.properties) {
              item.properties = [];
            }
            item.properties.hidden = isHidden;
          }
        });
        this.convertFlatDataToPageTree()
        return response
      })
    },

    async deleteProjectPage(payload: FeatureNs.UpdateProjectPageRequest
    ): Promise<FeatureNs.ProjectPage> {
      return ProjectPageApi.deleteProjectPage(payload)
      .then((response) => {
        payload.projectPage.ids.forEach((id: string) => this.projectPageMap.delete(id));
        this.convertFlatDataToPageTree()
        return response
      })
    },

    async addNewProjectPage(
        payload: FeatureNs.UpdateProjectPageRequest
    ): Promise<FeatureNs.ProjectPage> {
      return ProjectPageApi.addNewProjectPage(payload)
      .then((addedProjectPage) => {
        this.projectPageMap.set(addedProjectPage.id, addedProjectPage);
        this.convertFlatDataToPageTree();
        return addedProjectPage
      })
    },


    async updateProjectPage(
        payload: FeatureNs.UpdateProjectPageRequest
    ): Promise<CommonNs.AnyResponse> {
      return ProjectPageApi.updateProjectPage(payload)
      .then((response) => {
        return response
      })
    },

    async updateProjectPageOrder(
        payload: FeatureNs.UpdateProjectPageRequest
    ): Promise<FeatureNs.Feature> {
      return ProjectPageApi.updateProjectPageOrder(payload)
      .then((response) => {
        return response;
      })
    },

    convertFlatDataToPageTree() {
      const hierarchy: FeatureNs.PageTreeNode[] = [];
      this.projectPageMap.forEach((item: FeatureNs.ProjectPage, key: string) => {
        item.featureType = FeatureNs.FeatureTypeEnums.PAGE
        hierarchy.push({...item, ...item.properties, children: []} as FeatureNs.PageTreeNode);
      });

      this.projectPageTree = hierarchy;
      return hierarchy;
    },

    async searchFeatureInProjects(
        payload: FeatureNs.SearchFeatureRequest
    ): Promise<FeatureNs.SearchFeatureResponse[]> {
      return FeatureApi.searchFeatureInProjects(payload);
    },

    async importFeatureFromOtherProject(
        payload: FeatureNs.ImportFeatureRequest
    ): Promise<FeatureNs.Feature[]> {
      return FeatureApi.importFeatureFromOtherProject(payload).then((features) => {
        features.forEach((importedFeature) => this.featureMap.set(importedFeature.id, importedFeature))
        this.convertFlatDataToTree();
        return features;
      });
    },

    async refreshWaitingFeatures() {
      const workspaceStore = useWorkspaceStore()
      const projectStore = useSelectedProjectStore()
      if (projectStore.isJiraManualSyncActive) {
        setTimeout(async () => {
          await this.getWaitingFeatureToSend({
            workspaceId: workspaceStore.getSelectedWorkspaceId,
            projectId: projectStore.getSelectedProjectId
          });
        }, 2000)
      }
    },

    async getWaitingFeatureToSend(
        payload: FeatureNs.GetAllProjectPageRequest
    ): Promise<FeatureNs.Feature[]> {
      return FeatureApi.getWaitingFeatureToSend(payload).then((waitingFeatures) => {
        const featureIds = new Set(waitingFeatures.map((feature) => feature.id));
        const featureList = Array.from(this.featureMap.values()).filter((feature) => featureIds.has(feature.id))
        this.waitingFeatureCount = featureList.length;
        this.waitingFeatures = featureList;
        return featureList;
      });
    },

    async sendFeatureToJira(
        payload: FeatureNs.SendToJiraRequest
    ): Promise<CommonNs.AnyResponse> {
      return FeatureApi.sendToJira(payload);
    },

    async sendFeatureToAzure(
        payload: FeatureNs.SendToAzureRequest
    ): Promise<CommonNs.AnyResponse> {
      return AzureApi.sendFeatures(payload);
    },

    async getWaitingFeatureToSendAzure(
        payload: FeatureNs.GetAllProjectPageRequest
    ): Promise<FeatureNs.Feature[]> {
      return FeatureApi.getWaitingFeatureToSend(payload).then((waitingFeatures) => {
        const featureIds = new Set(waitingFeatures.map((feature) => feature.id));
        const featureList = Array.from(this.featureMap.values()).filter((feature) => featureIds.has(feature.id))
        return featureList;
      });
    },

    applyWebhookUpdate(issueKey: string, fields: Record<string, unknown>): void {
      for (const feature of this.featureMap.values()) {
        if (feature.intIssueKey === issueKey) {
          feature.intProperties = { ...(feature.intProperties ?? {}), ...fields }
          break
        }
      }
    },



  }

})
